// Availability pill for the top bar. Reads and writes the agent status through
// the existing dialer context (updateAgentStatus), so the persisted
// availability contract is unchanged: the context maps these words to the
// backend's available/busy/away/offline.

import React, { useContext, useEffect, useRef, useState } from "react";
import IntegratedDialerContext from "../../contexts/IntegratedDialerContext";
import { CheckIcon, ChevronDownIcon, XIcon } from "./icons";
import { MS, usePresence } from "../ui/motion";
import { focusRowUnderPointer } from "../ui/menuFocus";

export const AVAILABILITY_OPTIONS = [
  { value: "available", label: "Available", tone: "live" },
  { value: "busy", label: "Busy", tone: "warn" },
  { value: "away", label: "Away", tone: undefined },
  { value: "offline", label: "Offline", tone: "danger" },
];

/** Normalize the context's mixed-case status words for display. */
export const describeAvailability = (status) => {
  const word = String(status || "").toLowerCase();
  if (word === "on call") return { value: word, label: "On call", tone: "live" };
  if (word === "connecting") return { value: word, label: "Connecting", tone: "live" };
  if (word === "wrap-up") return { value: word, label: "Wrap-up", tone: "warn" };
  if (word === "break") return { value: "away", label: "Away", tone: undefined };
  return (
    AVAILABILITY_OPTIONS.find((option) => option.value === word) || {
      value: word,
      label: status || "Unknown",
      tone: undefined,
    }
  );
};

/** Availability state and setter, or null outside the dialer providers. */
export const useAvailability = () => {
  const dialer = useContext(IntegratedDialerContext);
  if (!dialer?.updateAgentStatus) return null;
  return {
    status: describeAvailability(dialer.agentStatus),
    setStatus: (value) => dialer.updateAgentStatus(value),
    save: dialer.agentStatusSave || "idle",
  };
};

const SAVED_MS = 1000;
const FAILED_MS = 2400;

/**
 * The pill's confirmation: after the user picks a status, "saved" shows a
 * tick for a second once the write resolves; "failed" marks the pill until
 * the next pick (or 2.4 s). Status changes the user did not make (a call
 * starting) never flash a tick.
 */
export const useSaveConfirmation = (save) => {
  // "armed" after a pick, "writing" once that pick's write has started. A
  // result left over from an earlier write ("saved" still in state) never
  // confirms the new pick early.
  const [phase, setPhase] = useState(null);
  const [shown, setShown] = useState(null);
  useEffect(() => {
    if (phase === "armed" && save === "saving") setPhase("writing");
    if (phase !== "writing" || (save !== "saved" && save !== "error")) return undefined;
    setPhase(null);
    setShown(save === "saved" ? "saved" : "failed");
    return undefined;
  }, [phase, save]);
  useEffect(() => {
    if (!shown) return undefined;
    const timer = setTimeout(() => setShown(null), shown === "saved" ? SAVED_MS : FAILED_MS);
    return () => clearTimeout(timer);
  }, [shown]);
  return {
    shown,
    expect: () => {
      setShown(null);
      setPhase("armed");
    },
  };
};

const AvailabilityPill = ({ compact = false }) => {
  const availability = useAvailability();
  const confirmation = useSaveConfirmation(availability?.save);
  const [open, setOpen] = useState(false);
  const presence = usePresence(open, { exitMs: MS.exit });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", onKey);
    rootRef.current?.querySelector('[role="menuitemradio"][aria-checked="true"]')?.focus();
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!availability) return null;
  const { status, setStatus } = availability;

  const onMenuKeyDown = (event) => {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const items = [...rootRef.current.querySelectorAll('[role="menuitemradio"]')];
    const index = items.indexOf(document.activeElement);
    let next = 0;
    if (event.key === "ArrowDown") next = (index + 1) % items.length;
    if (event.key === "ArrowUp") next = (index - 1 + items.length) % items.length;
    if (event.key === "End") next = items.length - 1;
    items[next]?.focus();
  };

  return (
    <div className="relative" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="sg-top-pill"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Availability: ${status.label}`}
        data-compact={compact || undefined}
        data-confirm={confirmation.shown || undefined}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="sg-dot" data-tone={status.tone} />
        {/* The word stays on phones too: a bare dot does not say what it means. */}
        <span>{status.label}</span>
        <span className="sg-top-pill-end" aria-hidden="true">
          <ChevronDownIcon size={12} className="sg-top-pill-chevron" />
          {confirmation.shown === "saved" && <CheckIcon size={12} className="sg-top-pill-tick" />}
          {confirmation.shown === "failed" && <XIcon size={12} className="sg-top-pill-fail" />}
        </span>
      </button>
      <span className="sr-only" role="status" aria-live="polite">
        {confirmation.shown === "saved" ? `Availability saved: ${status.label}` : ""}
        {confirmation.shown === "failed" ? "Availability could not be saved" : ""}
      </span>
      {presence.mounted && (
        <div
          role="menu"
          aria-label="Set availability"
          className="sg-menu-panel sg-drop sg-avail-menu right-0 w-48"
          data-state={presence.state}
          onAnimationEnd={presence.onAnimationEnd}
          onKeyDown={onMenuKeyDown}
          onMouseMove={focusRowUnderPointer(".sg-menu-row")}
        >
          <div className="sg-menu-head sg-micro">Availability</div>
          {AVAILABILITY_OPTIONS.map((option) => {
            const checked = status.value === option.value;
            return (
              <button
                key={option.value}
                type="button"
                role="menuitemradio"
                aria-checked={checked}
                className="sg-menu-row"
                onClick={() => {
                  if (!checked) {
                    confirmation.expect();
                    setStatus(option.value);
                  }
                  setOpen(false);
                  triggerRef.current?.focus();
                }}
              >
                <span className="sg-dot" data-tone={option.tone} />
                <span className="flex-1">{option.label}</span>
                {checked && <CheckIcon size={14} className="sg-menu-check" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AvailabilityPill;
